import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MobileMenuShapeTopSVG, MobileMenuShapesBottomSVG } from "../../../assets";
import { Icon, Image } from "@nubeteck/components";
import {
  MenuButtonStyled,
  MobileUserSectionStyled,
  MobileMenuStyled,
  MobileMenuContainerStyled,
  UserNameStyled,
  TopShapeStyled,
  BottomShapeStyled,
  MenuButton,
  LanguageSwitcherContainer,
} from "./mobile-menu.style";
import { UserHelp } from "src/components/other";
import LanguageSwitcher from "../../LanguageSwitcher";
import { Dropdown, Skeleton } from "antd";
import { MenuProps } from "antd/lib";
import { getLoginPage } from "src/Api/content";
import { useLocale } from "src/i18n/localeContext";
import { StrapiError } from "src/pages/StrapiError";

interface IMenuItem {
  name: string;
  link: string;
  icon: string;
}

interface IMenuMobile {
  menuItems: (IMenuItem | null)[];
  userName: string;
  isVisible: boolean;
  isHelpVisible: boolean;
  hide: () => void;
  logout: () => void;
  logoutImpersonated: () => void;
  isFakeSelecting: boolean;
  availableRoles: MenuProps;
  isATeacher: boolean;
  isAdmin: boolean;
}

interface IButtonProps {
  icon: JSX.Element;
  title: string;
  onClick?: () => void;
}

const MobileMenuButton = ({ icon, title, onClick }: IButtonProps) => {
  return (
    <MenuButton type="text" withIcon icon={icon} onClick={onClick}>
      {title}
    </MenuButton>
  );
};

const MobileMenu = ({
  menuItems,
  userName,
  isVisible,
  hide,
  isHelpVisible,
  logout,
  logoutImpersonated,
  isFakeSelecting,
  availableRoles,
  isATeacher,
  isAdmin,
}: IMenuMobile) => {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const [loginPage, setLoginPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setLoginPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getLoginPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setLoginPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setLoginPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });

    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setLoginPage(null);
      }}
    />;
  }

  return (
    <MobileMenuContainerStyled className={isVisible ? "is-visible" : ""}>
      {/* Botón de cierre flotante en la esquina superior derecha */}
      {/* <button 
        style={{
          position: 'absolute',
          top: '15px',
          right: '15px',
          zIndex: 40,
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '50%',
          width: '45px',
          height: '45px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '2px solid rgba(255, 255, 255, 0.5)',
          padding: 0,
          outline: 'none'
        }} 
        onClick={hide} 
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            hide();
          }
        }}
        title="Cerrar menú"
        aria-label="Cerrar menú"
        type="button"
      >
        <Icon name="close" size={24} color="white" />
      </button> */}
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%',
        padding: '0 20px',
        marginBottom: '20px',
        position: 'relative',
        zIndex: 25
      }}>
        <MenuButtonStyled 
          type="text" 
          onClick={hide}
          title="Cerrar menú"
          style={{
            marginLeft: '10px',
            position: 'relative',
            zIndex: 30,
            cursor: 'pointer'
          }}
        >
          <Icon name="" size={32} />
        </MenuButtonStyled>
        <LanguageSwitcherContainer style={{ marginRight: '10px' }}>
          <LanguageSwitcher />
        </LanguageSwitcherContainer>
      </div>
      <UserNameStyled>
        <Icon name="" /> {userName}
      </UserNameStyled>
      <MobileMenuStyled>
        {menuItems.map(
          (item) =>
            item && (
              <li key={item.name}>
                <NavLink
                  onClick={
                    isFakeSelecting
                      ? () => {
                          logoutImpersonated();
                          navigate(item.link);
                          window.location.reload();
                          hide();
                        }
                      : () => hide()
                  }
                  to={item.link}
                  end={true}
                  className={({ isActive }) => (isActive ? "is-active" : "")}
                >
                  <Icon name={item.icon} /> {item.name}
                </NavLink>
              </li>
            ),
        )}
        <li>
          <MobileUserSectionStyled>
            {/* <Notifications /> */}
            {isHelpVisible && <UserHelp />}

            {/* Role Selector */}
            <div style={{ padding: '10px 40px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Dropdown menu={availableRoles} trigger={["click"]}>
                <div style={{ color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px' }}>
                  <Icon name="" />
                  <span>{isATeacher ? "Docente" : isAdmin ? "Admin" : "Estudiante"}</span>
                  <Icon name="" />
                </div>
              </Dropdown>
            </div>

            <MobileMenuButton icon={<Icon name="" />} title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (loginPage?.log_out || "Cerrar sesión")} onClick={logout} />
          </MobileUserSectionStyled>
        </li>
      </MobileMenuStyled>
      <TopShapeStyled>
        <Image src={MobileMenuShapeTopSVG} width={80} className="top-shape" preview={false} />
      </TopShapeStyled>
      <BottomShapeStyled>
        <Image
          src={MobileMenuShapesBottomSVG}
          width={150}
          className="bottom-shape"
          preview={false}
        />
      </BottomShapeStyled>
    </MobileMenuContainerStyled>
  );
};

export default MobileMenu;
